import React, { useState, useEffect } from "react";
import {
  CheckCircle,
  XCircle,
  Trophy,
  Code,
  AlertTriangle,
  Clock,
} from "lucide-react";
import { styles } from "../utils/globalStyles";

export default function GameEngine({ questData, onFinish, onExit }) {
  // 1. DATA PREP
  let rawContent = questData?.konten || [];
  if (typeof rawContent === "string") {
    try {
      rawContent = JSON.parse(rawContent);
    } catch (e) {
      rawContent = [];
    }
  }
  const firstItem = rawContent[0] || {};
  const isCustomGame = firstItem.type === "CUSTOM_HTML";
  const durasiMenit = questData.durasi_menit || 0; // 0 = No Timer

  // 2. STATE
  const [indexSoal, setIndexSoal] = useState(0);
  const [skor, setSkor] = useState(0);
  const [selesai, setSelesai] = useState(false);
  const [lulus, setLulus] = useState(false);
  const [alasanSelesai, setAlasanSelesai] = useState("NORMAL"); // NORMAL | WAKTU_HABIS

  // STATE TIMER
  // Jika durasi 0, set null (artinya infinite)
  const [timeLeft, setTimeLeft] = useState(
    durasiMenit > 0 ? durasiMenit * 60 : null
  );

  // --- EFFECT: TIMER COUNTDOWN ---
  useEffect(() => {
    if (selesai) return; // Stop timer kalau sudah selesai
    if (timeLeft === null) return; // Stop kalau tidak ada batas waktu

    if (timeLeft <= 0) {
      handleTimeOut();
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, selesai]);

  // --- FORMAT WAKTU (MM:SS) ---
  const formatTime = (seconds) => {
    if (seconds === null) return "";
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  // --- HANDLE WAKTU HABIS ---
  const handleTimeOut = () => {
    // Paksa selesai dengan skor saat ini
    setAlasanSelesai("WAKTU_HABIS");

    // Hitung skor kuis saat ini (jika mode kuis)
    let finalScore = skor;
    if (!isCustomGame) {
      finalScore = Math.round((skor / rawContent.length) * 100);
    }

    // Biasanya kalau waktu habis, dianggap gagal atau lulus pas-pasan (tergantung kebijakan)
    // Di sini kita pakai logika standar: Cek apakah skor sementara sudah cukup?
    selesaikanGame(finalScore);
  };

  // --- LISTENER CUSTOM GAME ---
  useEffect(() => {
    if (isCustomGame) {
      const messageHandler = (event) => {
        if (event.data && event.data.type === "GAME_FINISH") {
          const { score, passed } = event.data;
          selesaikanGame(score, passed);
        }
      };
      window.addEventListener("message", messageHandler);
      return () => window.removeEventListener("message", messageHandler);
    }
  }, [isCustomGame]);

  // --- LOGIKA UTAMA SELESAI ---
  const selesaikanGame = (finalScore, isPassed) => {
    let passedStatus = isPassed;
    if (passedStatus === undefined) {
      passedStatus = finalScore >= (questData.min_score || 70);
    }
    setSkor(finalScore);
    setLulus(passedStatus);
    setSelesai(true);
    onFinish(finalScore, passedStatus);
  };

  // --- LOGIKA QUIZ ---
  const handleJawabQuiz = (jawaban) => {
    const currentSoal = rawContent[indexSoal];
    let newSkor = skor;
    if (jawaban === currentSoal.kunci) newSkor = skor + 1;

    if (indexSoal + 1 < rawContent.length) {
      setIndexSoal(indexSoal + 1);
      setSkor(newSkor);
    } else {
      let finalPoints = newSkor;
      if (jawaban === currentSoal.kunci) finalPoints = skor + 1;
      const nilaiSkala100 = Math.round((finalPoints / rawContent.length) * 100);
      selesaikanGame(nilaiSkala100);
    }
  };

  // === RENDER ===

  // 1. TAMPILAN TIMER
  const renderTimer = () => {
    if (timeLeft === null) return null;
    const isCritical = timeLeft < 30; // Merah jika < 30 detik
    return (
      <div
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          background: isCritical ? "#FECACA" : "#E0F2FE",
          color: isCritical ? "#DC2626" : "#0284C7",
          padding: "5px 10px",
          borderRadius: 20,
          fontSize: 12,
          fontWeight: "bold",
          display: "flex",
          alignItems: "center",
          gap: 5,
          boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
        }}
      >
        <Clock size={14} className={isCritical ? "animate-pulse" : ""} />
        {formatTime(timeLeft)}
      </div>
    );
  };

  // 2. TAMPILAN SELESAI
  if (selesai) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        {alasanSelesai === "WAKTU_HABIS" && (
          <div
            style={{
              background: "#FEE2E2",
              color: "#EF4444",
              padding: 10,
              borderRadius: 8,
              marginBottom: 20,
              fontWeight: "bold",
            }}
          >
            ⌛ WAKTU HABIS!
          </div>
        )}

        {lulus ? (
          <Trophy
            size={60}
            color="#EAB308"
            style={{ marginBottom: 20, display: "inline-block" }}
          />
        ) : (
          <XCircle
            size={60}
            color="#EF4444"
            style={{ marginBottom: 20, display: "inline-block" }}
          />
        )}
        <h2 style={{ margin: "0 0 10px 0" }}>
          {lulus ? "Misi Berhasil!" : "Gagal Lulus"}
        </h2>
        <p style={{ fontSize: 18, margin: 0 }}>
          Nilai Kamu: <strong>{skor}</strong>
        </p>
        <p style={{ color: "#64748B", fontSize: 14 }}>
          Minimal Lulus: {questData.min_score || 70}
        </p>
        <button
          onClick={onExit}
          style={{ ...styles.primaryBtn, marginTop: 20 }}
        >
          Tutup
        </button>
      </div>
    );
  }

  // 3. TAMPILAN CUSTOM GAME
  if (isCustomGame) {
    const rawHtml = firstItem.html || "";
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          minHeight: "450px",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {renderTimer()}
        <div
          style={{
            background: "#F8FAFC",
            padding: "8px 15px",
            fontSize: 12,
            color: "#64748B",
            borderBottom: "1px solid #E2E8F0",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Code size={14} /> Mode Interaktif {durasiMenit > 0 && "• Berwaktu"}
        </div>
        <iframe
          title="Custom Game"
          srcDoc={rawHtml}
          style={{ width: "100%", flex: 1, border: "none" }}
          sandbox="allow-scripts allow-same-origin allow-popups allow-modals"
        />
      </div>
    );
  }

  // 4. TAMPILAN QUIZ
  const currentSoal = rawContent[indexSoal];
  if (!currentSoal || !Array.isArray(currentSoal.opsi)) {
    return (
      <div style={{ textAlign: "center", padding: 40, color: "#64748B" }}>
        <AlertTriangle size={40} color="#F59E0B" style={{ marginBottom: 10 }} />
        <p>Gagal Memuat Game</p>
        <button onClick={onExit} style={{ marginTop: 20 }}>
          Kembali
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: 10, position: "relative" }}>
      {renderTimer()} {/* TIMER DI POJOK KANAN */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 20,
          borderBottom: "1px solid #eee",
          paddingBottom: 10,
          paddingRight: 80,
        }}
      >
        <span style={{ fontWeight: "bold", color: "#64748B" }}>
          Soal {indexSoal + 1} / {rawContent.length}
        </span>
        {/* Judul disembunyikan jika layar kecil agar tidak nabrak timer */}
      </div>
      <h3 style={{ fontSize: 20, marginBottom: 20 }}>{currentSoal.tanya}</h3>
      <div style={{ display: "grid", gap: 10 }}>
        {currentSoal.opsi.map((opsi, idx) => (
          <button
            key={idx}
            onClick={() => handleJawabQuiz(opsi)}
            style={{
              padding: "15px",
              borderRadius: "8px",
              border: "1px solid #CBD5E1",
              background: "white",
              fontSize: "16px",
              cursor: "pointer",
              textAlign: "left",
              transition: "0.2s",
            }}
            onMouseOver={(e) => (e.target.style.background = "#F1F5F9")}
            onMouseOut={(e) => (e.target.style.background = "white")}
          >
            {opsi}
          </button>
        ))}
      </div>
    </div>
  );
}
